<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Online Orders</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Orders completed through the quote calculator's checkout</p>
      </div>
    </div>

    <!-- Filters Card -->
    <v-card class="mb-6 pa-4">
      <div class="d-flex align-center flex-wrap" style="gap: 16px;">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search by name, email, or phone"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 400px;"
          clearable
        ></v-text-field>
        <v-select
          v-model="paymentStatus"
          :items="paymentStatusOptions"
          label="Payment"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
        ></v-select>
        <v-select
          v-model="checkoutMatch"
          :items="checkoutMatchOptions"
          label="Checkout"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
        ></v-select>
        <v-switch
          v-model="excludeTest"
          label="Hide test payments"
          color="primary"
          density="compact"
          hide-details
        ></v-switch>
      </div>
    </v-card>

    <!-- Data Table Card -->
    <v-card>
      <v-data-table-server
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :headers="headers"
        :items="orders"
        :items-length="total"
        :items-per-page-options="[20, 50, 100]"
        :loading="loading"
        :search="search"
        @update:options="loadItems"
      >
        <template #item.paidAt="{ item }">
          <span v-if="item.paidAt">{{ formatDate(item.paidAt) }}</span>
          <span v-else class="text-grey">--</span>
        </template>

        <template #item.orderNumber="{ item }">
          <div>
            <div class="font-weight-medium">{{ item.orderNumber }}</div>
            <div v-if="item.foxyDisplayId" class="text-caption text-grey">Foxy #{{ item.foxyDisplayId }}</div>
          </div>
        </template>

        <template #item.customer="{ item }">
          <div v-if="item.customer">
            <div class="font-weight-medium">{{ item.customer.name }} {{ item.customer.lastName }}</div>
            <div class="text-caption text-grey">{{ item.customer.email }}</div>
          </div>
          <span v-else class="text-grey">--</span>
        </template>

        <template #item.event="{ item }">
          <div>
            <div>{{ eventDate(item) || '--' }}</div>
            <div v-if="item.deliveryWindow" class="text-caption text-grey">{{ item.deliveryWindow }}</div>
          </div>
        </template>

        <template #item.service="{ item }">
          <div>
            <div>{{ item.service || item.delivery || '--' }}</div>
            <div v-if="item.eventCity" class="text-caption text-grey text-capitalize">{{ item.eventCity }}</div>
          </div>
        </template>

        <template #item.guests="{ item }">
          {{ item.finalNumberOfGuests ?? item.numberOfGuests ?? '--' }}
        </template>

        <template #item.amountPaid="{ item }">
          <span v-if="item.amountPaid != null" class="font-weight-medium">${{ formatCurrency(item.amountPaid) }}</span>
          <span v-else class="text-grey">--</span>
        </template>

        <template #item.paymentStatus="{ item }">
          <PaymentStatusChip :quote="{ ...item, paymentIsTest: item.isTest }" />
        </template>

        <template #item.orderStatus="{ item }">
          <v-chip :color="ORDER_STATUSES[item.orderStatus]?.color" size="small" variant="tonal">
            {{ ORDER_STATUSES[item.orderStatus]?.label || item.orderStatus }}
          </v-chip>
        </template>

        <template #item.actions="{ item }">
          <v-btn
            density="compact"
            icon="mdi-eye"
            variant="text"
            :disabled="!item.customer"
            @click="viewOrder(item)"
          ></v-btn>
        </template>
      </v-data-table-server>
      <div v-if="!orders.length && !loading" class="text-center py-8 text-medium-emphasis">
        No online orders found.
      </div>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { orderService } from '../services';
import { useNotificationStore } from '../stores';
import { useDebounceSearch } from '../composables/useDebounceSearch';
import { formatDate } from '../utils';
import PaymentStatusChip from '../components/PaymentStatusChip.vue';

defineOptions({ name: 'OnlineOrdersView' });

const router = useRouter();
const notificationStore = useNotificationStore();

const orders = ref([]);
const search = ref('');
const paymentStatus = ref('');
const checkoutMatch = ref('');
const excludeTest = ref(false);
const page = ref(1);
const itemsPerPage = ref(20);
const total = ref(0);
const loading = ref(false);

const paymentStatusOptions = [
  { title: 'Any payment', value: '' },
  { title: 'Checkout completed', value: 'paid' },
  { title: 'Refunded', value: 'refunded' },
  { title: 'Voided', value: 'voided' },
];

// Was the paid Foxy cart the one that was signed?
const checkoutMatchOptions = [
  { title: 'Any checkout', value: '' },
  { title: 'Changed at checkout', value: 'changed' },
  { title: 'Paid as quoted', value: 'unchanged' },
  { title: 'Not compared', value: 'unknown' },
];

const ORDER_STATUSES = {
  pending: { label: 'Pending', color: 'grey' },
  confirmed: { label: 'Confirmed', color: 'primary' },
  in_progress: { label: 'In Progress', color: 'info' },
  completed: { label: 'Completed', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'error' },
};

const headers = [
  { title: 'Paid', key: 'paidAt', sortable: false },
  { title: 'Order', key: 'orderNumber', sortable: false },
  { title: 'Customer', key: 'customer', sortable: false },
  { title: 'Event', key: 'event', sortable: false },
  { title: 'Service', key: 'service', sortable: false },
  { title: 'Guests', key: 'guests', sortable: false, align: 'end' },
  { title: 'Amount Paid', key: 'amountPaid', sortable: false, align: 'end' },
  { title: 'Payment', key: 'paymentStatus', sortable: false },
  { title: 'Order Status', key: 'orderStatus', sortable: false },
  { title: '', key: 'actions', sortable: false, width: '50px' },
];

async function loadOrders(targetPage = 1, pageSize = 20) {
  loading.value = true;
  try {
    const params = {
      page: targetPage,
      limit: pageSize,
      search: search.value || '',
      paidOnline: 'true',
    };
    if (paymentStatus.value) params.paymentStatus = paymentStatus.value;
    if (checkoutMatch.value) params.checkoutMatch = checkoutMatch.value;
    if (excludeTest.value) params.excludeTest = 'true';
    const data = await orderService.getOrders(params);
    orders.value = data.data;
    total.value = data.total;
    page.value = data.page;
    itemsPerPage.value = pageSize;
  } catch (err) {
    notificationStore.handleApiError(err, 'Failed to load online orders');
  } finally {
    loading.value = false;
  }
}

useDebounceSearch(search, () => {
  page.value = 1;
  loadOrders(1, itemsPerPage.value);
});

watch([paymentStatus, checkoutMatch, excludeTest], () => {
  page.value = 1;
  loadOrders(1, itemsPerPage.value);
});

function loadItems(options) {
  page.value = options.page;
  itemsPerPage.value = options.itemsPerPage;
  loadOrders(page.value, itemsPerPage.value);
}

function viewOrder(order) {
  router.push({ name: 'CustomerOrderView', params: { customerId: order.customer.id, orderId: order.id } });
}

// The date Foxy's checkout carried, else the quote's event date as entered.
// The Foxy date is stored as midnight UTC, so read it in UTC to keep the day.
function eventDate(order) {
  if (!order.finalDeliveryDate) return order.event?.date || '';
  return new Date(order.finalDeliveryDate).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

function formatCurrency(amount) {
  if (amount == null) return '0.00';
  return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
</script>

<style scoped>
:deep(.v-data-table) {
  border-radius: 0 0 12px 12px;
}

:deep(.v-data-table-header) {
  background-color: #f8fafc;
}

:deep(.v-data-table-header th) {
  font-weight: 600 !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 0.5px;
  color: #64748b !important;
}
</style>
